import { findClosestMatchFromObject } from '@/lib/search-utils';
import { Lang, LANGUAGES, ALLOWED_PERMISSIONS_FOR_CHANGE } from 'shared/constants';
import { useProfileFields } from 'shared/hooks/profile-fields.hooks';
import { PARENT_FIELD_BY_FIELD } from 'shared/constants/profile-fields.constants';
import { createContext, useState, useEffect, useMemo } from 'react';
import * as z from 'zod';
import { getLabels /*getRotations*/ } from '../api-helper';
import { STAGES, useUpload } from '../../csv-importer/use-upload';
import Checkbox from '../../checkbox';
import { Loader } from '@/components/loader';
import userServiceApi from 'shared/api/user.service.api';
import { useParams } from 'react-router-dom';

export const UploadContext = createContext();

const ALLOWED_PERMISSIONS = Object.fromEntries(ALLOWED_PERMISSIONS_FOR_CHANGE.map(p => [p, p]));

const PERMISSIONS_LABELS = {
  ACCOUNT_ADMIN: 'Account Admin',
  COLLABORATOR: 'Collaborator',
  VIEWER: 'Viewer',
};

// Per-org customizable profile fields (GET /profile-fields)
const PROFILE_FIELD_KEYS = [
  'department',
  'profession',
  'role',
  'levelOfTraining',
  'division',
  'specialty',
  'unit',
];
const MULTI_PROFILE_FIELDS = new Set(['division', 'specialty', 'unit']);

/**
 * Creates an array of objects with key-value pairs for each option
 *
 * @param {*} options
 * @param {*} lang
 * @returns an array of objects with key-value pairs for each option
 */
const createOptions = (options, lang) =>
  Object.entries(options).map(([optionKey, optionValue]) => ({
    key: optionKey,
    value: lang[optionValue],
  }));

const filterEmptyValues = val => (Array.isArray(val) ? val.filter(item => item !== '') : val);

/**
 * Filters the keys of an object based on another object's keys.
 *
 * @param {Object} source - The object to filter.
 * @param {Object} keysObject - The object whose keys will be used for filtering.
 * @returns {Object} A new object with only the keys present in keysObject.
 */
function filterObjectKeys(source, keysObject) {
  return Object.fromEntries(Object.entries(source).filter(([key]) => key in keysObject));
}

// Static (non-per-org) helpers — language stays a global enum; permissions are fixed roles.
const languageFind = findClosestMatchFromObject(filterObjectKeys(Lang.PROFILE_OPTIONS, LANGUAGES));
const permissionsFind = findClosestMatchFromObject(PERMISSIONS_LABELS);

/** Flatten the grouped `GET /profile-fields` response into a `{ [fieldKey]: field }` lookup. */
const buildFieldsByKey = byGroup => {
  const all = byGroup ? [...(byGroup.ORGANIZATIONAL || []), ...(byGroup.PROFESSIONAL || [])] : [];
  return all.reduce((acc, field) => {
    acc[field.fieldKey] = field;
    return acc;
  }, {});
};

const hasOptions = (fieldsByKey, key) => !!fieldsByKey[key]?.options?.length;

const buildPropertyOptions = fieldsByKey => {
  const fieldLabel = key => fieldsByKey[key]?.displayLabel;
  const options = {
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Phone',
    pager: 'Pager',
    cisco: 'Cisco',
    profession: fieldLabel('profession'),
    role: fieldLabel('role'),
    department: fieldLabel('department'),
    language: 'Language',
    timezone: 'Timezone',
    levelOfTraining: fieldLabel('levelOfTraining'),
    title: 'Title',
    division: fieldLabel('division'),
    location: 'Location',
    specialty: fieldLabel('specialty'),
    unit: fieldLabel('unit'),
    permissions: 'Permissions',
  };
  PROFILE_FIELD_KEYS.forEach(key => {
    if (!hasOptions(fieldsByKey, key)) delete options[key];
  });
  return options;
};

/** Dropdown options + fuzzy CSV-value matchers, keyed by property. */
const buildProfessionalSelectOptions = (fieldsByKey, locationOptions, locationFindMatch) => {
  const result = {};
  PROFILE_FIELD_KEYS.forEach(key => {
    const field = fieldsByKey[key];
    if (!field?.options?.length) return;
    const labelByCode = field.options.reduce((acc, option) => {
      acc[option.code] = option.label;
      return acc;
    }, {});
    result[key] = {
      findMatch: findClosestMatchFromObject(labelByCode),
      options: field.options.map(option => ({ key: option.code, value: option.label })),
      ...(MULTI_PROFILE_FIELDS.has(key) ? { multiple: true } : {}),
    };
  });
  result.language = {
    findMatch: languageFind,
    options: createOptions(LANGUAGES, Lang.PROFILE_OPTIONS),
  };
  result.location = {
    findMatch: locationFindMatch,
    options: locationOptions,
    multiple: true,
  };
  result.permissions = {
    findMatch: permissionsFind,
    options: createOptions(ALLOWED_PERMISSIONS, PERMISSIONS_LABELS),
  };
  return result;
};

/** Cross-field check: a dependent value must belong to its selected parent option. */
const buildDependencyRefine = fieldsByKey => (user, ctx) => {
  Object.entries(PARENT_FIELD_BY_FIELD).forEach(([childKey, parentKey]) => {
    const childCode = user[childKey];
    if (!childCode) return;
    const childOption = fieldsByKey[childKey]?.options?.find(o => o.code === childCode);
    // No hierarchy configured for this option → nothing to enforce.
    if (!childOption?.parentOptionId) return;
    const parentOption = fieldsByKey[parentKey]?.options?.find(o => o.code === user[parentKey]);
    if (!parentOption || parentOption._id !== childOption.parentOptionId) {
      ctx.addIssue({
        path: [childKey],
        code: z.ZodIssueCode.custom,
        message: parentOption
          ? `Invalid for the selected ${parentKey}`
          : `Requires a matching ${parentKey}`,
      });
    }
  });
};

const enumField = codes =>
  z
    .enum(codes.concat(''), {
      errorMap: ({ received }) => ({ message: `Invalid: ${received}` }),
    })
    .optional()
    .nullable();

const multiEnumField = codes =>
  z.preprocess(
    filterEmptyValues,
    z
      .array(
        z.enum(codes, {
          errorMap: ({ received }) => ({ message: `Invalid: ${received}` }),
        }),
      )
      .optional()
      .nullable(),
  );

const buildValidationSchema = fieldsByKey => {
  const shape = {
    firstName: z.string().optional().nullable(),
    lastName: z.string().optional().nullable(),
    email: z.string().email('Invalid email format'),
    phone: z
      .string()
      .or(z.number())
      .optional()
      .nullable()
      .transform(value => value?.toString()),
    pager: z
      .string()
      .or(z.number())
      .optional()
      .nullable()
      .transform(value => value?.toString()),
    cisco: z
      .string()
      .or(z.number())
      .optional()
      .nullable()
      .transform(value => value?.toString()),
    language: z.string().optional().nullable(),
    timezone: z.string().optional().nullable(),
    title: z.string().optional().nullable(),
    location: z.preprocess(filterEmptyValues, z.array(z.string()).optional().nullable()),
    permissions: z
      .enum(ALLOWED_PERMISSIONS_FOR_CHANGE.concat(''), {
        errorMap: ({ received }) => ({ message: `Invalid: ${received}` }),
      })
      .optional()
      .nullable(),
  };

  PROFILE_FIELD_KEYS.forEach(key => {
    if (!hasOptions(fieldsByKey, key)) return;
    const codes = fieldsByKey[key].options.map(option => option.code);
    shape[key] = MULTI_PROFILE_FIELDS.has(key) ? multiEnumField(codes) : enumField(codes);
  });

  return z.object({
    users: z.array(z.object(shape).superRefine(buildDependencyRefine(fieldsByKey))),
  });
};

/** Location dropdown options (with color badge) + a fuzzy matcher, from the org labels API. */
const buildLocationOptions = locations => {
  if (!locations.length) return { locationOptions: [], locationFindMatch: null };
  const locationOptions = locations.map(label => ({
    key: label.code,
    value: (
      <p>
        {label.name}
        <span
          style={{
            backgroundColor: label.color,
          }}
          className="text-white px-1 py-0.5 ml-2 rounded-[0.25rem] font-semibold">
          {label.code}
        </span>
      </p>
    ),
  }));
  const locationLabels = locations.reduce((obj, { code, name }) => {
    obj[code] = name;
    return obj;
  }, {});
  const locationFindMatch = findClosestMatchFromObject(locationLabels, { keys: ['key', 'value'] });
  return { locationOptions, locationFindMatch };
};

export const UploadUsersProvider = ({ children }) => {
  const { data: profileFields, isLoading, isError } = useProfileFields();

  const [locations, setLocations] = useState([]);
  // Fetch locations
  useEffect(() => {
    getLabels().then(setLocations);
  }, []);

  const { id: organizationId } = useParams();
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [enforceWelcomeEmail, setEnforceWelcomeEmail] = useState(false);

  const fieldsByKey = useMemo(() => buildFieldsByKey(profileFields), [profileFields]);

  const { locationOptions, locationFindMatch } = useMemo(
    () => buildLocationOptions(locations),
    [locations],
  );

  const professionalSelectOptions = useMemo(
    () => buildProfessionalSelectOptions(fieldsByKey, locationOptions, locationFindMatch),
    [fieldsByKey, locationOptions, locationFindMatch],
  );

  const propertyOptions = useMemo(() => buildPropertyOptions(fieldsByKey), [fieldsByKey]);

  const validationSchema = useMemo(() => buildValidationSchema(fieldsByKey), [fieldsByKey]);

  const { showResults, stage, ...rest } = useUpload({
    propertiesMap: professionalSelectOptions,
  });

  const onSubmit = async data => {
    try {
      const results = await userServiceApi.importUsers({
        users: data.users,
        organizationId,
        skipEmail: !sendWelcomeEmail,
        enforceEmail: sendWelcomeEmail && enforceWelcomeEmail,
      });
      showResults({
        ...results.data,
        updated: results.data.updated.map(email => ({
          email,
          message: 'User updated successfully',
        })),
        new: results.data.new.map(email => ({
          email,
          message: 'User created successfully',
        })),
        failed: results.data.failed.map(({ email, error }) => ({
          email,
          message: error,
        })),
      });
    } catch (error) {
      console.error(error);
    }
  };

  // Profile fields are the source of truth for validation — don't render the
  // wizard (and let useForm capture a stale/empty schema) until they load.
  if (isLoading) return <Loader />;
  if (isError)
    return (
      <div className="p-6 text-red-500">
        Failed to load this organization&apos;s profile fields. Please close and retry.
      </div>
    );

  return (
    <UploadContext.Provider
      value={{
        ...rest,
        stage,
        validationSchema,
        propertyOptions,
        onSubmit,
        hasLocations: locations.length > 0,
        CustomPropsRender: (
          <>
            {stage === STAGES.VALIDATE && (
              <>
                <Checkbox
                  onChange={() => setSendWelcomeEmail(!sendWelcomeEmail)}
                  value={sendWelcomeEmail}
                  label={'Send welcome email'}
                />
                {sendWelcomeEmail && (
                  <div className="flex ml-6">
                    <Checkbox
                      onChange={() => setEnforceWelcomeEmail(!enforceWelcomeEmail)}
                      value={enforceWelcomeEmail}
                      label={
                        'Send welcome email to all the user list (by leaving this box unchecked, you will only send welcome emails to new users)'
                      }
                    />
                  </div>
                )}
              </>
            )}
          </>
        ),
      }}>
      {children}
    </UploadContext.Provider>
  );
};
